import type {
  Project,
  Preventivo,
  Finanziamento,
  Notaio,
  EstimateLevel,
} from '../types';
import { parseDecimal } from '../utils/pricing';

export interface DashboardTotals {
  immobile: number;
  lavori: number;
  notaioTasse: number;
  totaleComplessivo: number;
  finanziamenti: number;
  liquiditaNecessaria: number;
}

export interface CategoryBreakdown {
  categoria: string;
  totale: number;
  items: number;
}

/**
 * Calculate all dashboard totals based on estimate level
 */
export function calculateDashboardTotals(
  project: Project | null,
  level: EstimateLevel
): DashboardTotals {
  if (!project) {
    return {
      immobile: 0,
      lavori: 0,
      notaioTasse: 0,
      totaleComplessivo: 0,
      finanziamenti: 0,
      liquiditaNecessaria: 0,
    };
  }

  // Calculate lavori total from preventivi
  const lavori = calculatePreventiviTotal(project.preventivi || [], level);

  // Calculate notaio/tasse total
  const notaioTasse = calculateNotaioTotal(project.notaio);

  // Calculate immobile (prezzo acquisto from notaio)
  const immobile = project.notaio ? parseDecimal(project.notaio.prezzoAcquisto) : 0;

  // Calculate total finanziamenti
  const finanziamenti = calculateFinanziamentiTotal(project.finanziamenti || []);

  // Calculate totale complessivo
  const totaleComplessivo = immobile + lavori + notaioTasse;

  // Calculate liquidità necessaria (what you need to pay out of pocket)
  const liquiditaNecessaria = Math.max(0, totaleComplessivo - finanziamenti);

  return {
    immobile,
    lavori,
    notaioTasse,
    totaleComplessivo,
    finanziamenti,
    liquiditaNecessaria,
  };
}

/**
 * Calculate total from preventivi based on estimate level
 */
export function calculatePreventiviTotal(
  preventivi: Preventivo[],
  level: EstimateLevel
): number {
  const key = `costo${level.charAt(0).toUpperCase() + level.slice(1)}` as 'costoMin' | 'costoMed' | 'costoMax';

  return preventivi.reduce((sum, p) => {
    return sum + parseDecimal(p[key]);
  }, 0);
}

/**
 * Calculate total notaio costs
 */
export function calculateNotaioTotal(notaio: Notaio | null | undefined): number {
  if (!notaio) return 0;

  return (
    parseDecimal(notaio.onorarioNotaio) +
    parseDecimal(notaio.impostaRegistro) +
    parseDecimal(notaio.impostaCatastale) +
    parseDecimal(notaio.impostaIpotecaria) +
    parseDecimal(notaio.iva) +
    parseDecimal(notaio.speseAccessorie)
  );
}

/**
 * Calculate total finanziamenti
 */
export function calculateFinanziamentiTotal(finanziamenti: Finanziamento[]): number {
  return finanziamenti.reduce((sum, f) => sum + parseDecimal(f.importo), 0);
}

/**
 * Get preventivi grouped by category with totals
 */
export function getPreventiviByCategory(
  preventivi: Preventivo[],
  level: EstimateLevel
): CategoryBreakdown[] {
  const key = `costo${level.charAt(0).toUpperCase() + level.slice(1)}` as 'costoMin' | 'costoMed' | 'costoMax';

  const grouped = preventivi.reduce((acc, p) => {
    const cat = p.categoria;
    if (!acc[cat]) {
      acc[cat] = { totale: 0, items: 0 };
    }
    acc[cat].totale += parseDecimal(p[key]);
    acc[cat].items += 1;
    return acc;
  }, {} as Record<string, { totale: number; items: number }>);

  return Object.entries(grouped)
    .map(([categoria, data]) => ({
      categoria,
      ...data,
    }))
    .sort((a, b) => b.totale - a.totale);
}

/**
 * Calculate total monthly payment (rata) from all finanziamenti
 */
export function calculateTotalRataMensile(finanziamenti: Finanziamento[]): number {
  return finanziamenti.reduce((sum, f) => {
    return sum + parseDecimal(f.rataMensile);
  }, 0);
}

/**
 * Calculate percentage of project financed
 */
export function calculateFinancingPercentage(
  totaleComplessivo: number,
  finanziamenti: number
): number {
  if (totaleComplessivo === 0) return 0;
  return Math.min(1, finanziamenti / totaleComplessivo);
}
